import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import BWAccordion from 'components/shared/BWAccordion/index';
import FormItem from 'components/shared/BWFormControl/FormItem';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormTextArea from 'components/shared/BWFormControl/FormTextArea';
import FormRangePicker from 'components/shared/BWFormControl/FormDateRange';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import ProcurementHintBox from 'components/shared/ProcurementHintBox';

import { getOptionsGlobal } from 'actions/global';
import { mapDataOptions4Select } from 'utils/helpers';
import {
  AccountingTreatmentOptions,
  ApplyPartnerTypeCode,
  ApplyPartnerTypeOptions,
  DebitTypeOptions,
  DiscountBasisOptions,
  ProcurementDiscountTypeOptions,
  RecognitionTimingOptions,
  SettlementPartyOptions,
  SettlementDocumentOptions,
  VatAdjustmentModeCode,
  VatAdjustmentModeOptions,
  AccountingTreatmentCode,
  AffectsInventoryCostOptions,
  CostAllocationMethodCode,
  CostAllocationMethodOptions,
} from 'pages/DiscountProgram/ultils/constant';
import styled from 'styled-components';
import { allBusiness } from 'pages/Promotions/utils/constants';
import { mapArrayGetKey } from 'pages/Promotions/utils/helpers';
import useGetOptions, { optionType } from 'hooks/useGetOptions';

const FormItemWrapper = styled.div`
  .bw_frm_box {
    min-height: 79px;
  }
`;

const DiscountProgramInformation = ({ disabled }) => {
  const methods = useFormContext();
  const { setValue, watch } = methods;
  const { manufacturerData, businessData, areaData } = useSelector((state) => state.global);
  const supplierOptions = useGetOptions(optionType.supplier);
  const accountingAccountOptions = useGetOptions(optionType.accountingAccount, { labelName: 'display_name' });
  const applyPartnerTypeCode = watch('apply_partner_type_code');
  const accountingTreatmentCode = watch('accounting_treatment_code');
  const vatAdjustmentModeCode = watch('vat_adjustment_mode_code');
  const affectsInventoryCost = Number(watch('affects_inventory_cost') || 0) ? 1 : 0;
  const isSupplierMode =
    applyPartnerTypeCode === ApplyPartnerTypeCode.SUPPLIER || applyPartnerTypeCode === ApplyPartnerTypeCode.BOTH;
  const isManufacturerMode =
    applyPartnerTypeCode === ApplyPartnerTypeCode.MANUFACTURER || applyPartnerTypeCode === ApplyPartnerTypeCode.BOTH;
  const requiresPayableAccount = [
    AccountingTreatmentCode.NET_ON_INVOICE,
    AccountingTreatmentCode.AP_CREDIT_NOTE,
    AccountingTreatmentCode.PAYMENT_DISCOUNT,
  ].includes(accountingTreatmentCode);
  const requiresReceivableAccount = accountingTreatmentCode === AccountingTreatmentCode.END_PERIOD_REBATE;
  const requiresVatAdjustmentAccount = vatAdjustmentModeCode === VatAdjustmentModeCode.REDUCE_INPUT_VAT;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getOptionsGlobal('manufacturer'));
  }, [dispatch]);

  const loadCompanyList = useCallback(() => {
    dispatch(getOptionsGlobal('business'));
  }, [dispatch]);
  useEffect(loadCompanyList, [loadCompanyList]);

  const loadAreaList = useCallback(() => {
    dispatch(getOptionsGlobal('area'));
  }, [dispatch]);
  useEffect(loadAreaList, [loadAreaList]);

  useEffect(() => {
    if (!affectsInventoryCost) {
      setValue('cost_allocation_method_code', CostAllocationMethodCode.NO_COST_ALLOCATION);
    }
  }, [affectsInventoryCost, setValue]);

  useEffect(() => {
    if (!accountingTreatmentCode) return;

    if (accountingTreatmentCode === AccountingTreatmentCode.NET_ON_INVOICE) {
      setValue('settlement_document_code', 'INVOICE');
      setValue('recognition_timing_code', 'ON_INVOICE');
    }

    if (accountingTreatmentCode === AccountingTreatmentCode.AP_CREDIT_NOTE) {
      setValue('settlement_document_code', 'CREDIT_NOTE');
      setValue('recognition_timing_code', 'ON_CREDIT_NOTE');
    }

    if (accountingTreatmentCode === AccountingTreatmentCode.END_PERIOD_REBATE) {
      setValue('settlement_document_code', 'REBATE_STATEMENT');
      setValue('recognition_timing_code', 'PERIOD_END');
      if (!watch('vat_adjustment_mode_code')) {
        setValue('vat_adjustment_mode_code', VatAdjustmentModeCode.NO_VAT_ADJUSTMENT);
      }
    }

    if (accountingTreatmentCode === AccountingTreatmentCode.PAYMENT_DISCOUNT) {
      setValue('settlement_document_code', 'PAYMENT_SETTLEMENT');
      setValue('recognition_timing_code', 'ON_PAYMENT');
      setValue('vat_adjustment_mode_code', VatAdjustmentModeCode.NO_VAT_ADJUSTMENT);
      setValue('settlement_party_code', ApplyPartnerTypeCode.SUPPLIER);
      setValue('affects_inventory_cost', 0);
      setValue('cost_allocation_method_code', CostAllocationMethodCode.NO_COST_ALLOCATION);
    }
  }, [accountingTreatmentCode, setValue, watch]);

  const handleChangeCompanyBusiness = (value, check) => {
    if (check) {
      setValue('business_id', value);
    } else {
      // setValue('company_id', value);
      setValue('business_id', []);
    }
  };

  return (
    <BWAccordion title='Thông tin chương trình chiết khấu'>
      <ProcurementHintBox title='Cách khai báo CT chiết khấu mua hàng'>
        Màn này dùng chung cho cả chiết khấu do nhà cung cấp đưa ra và chiết khấu do hãng tài trợ. Chốt theo đúng 4
        nhóm nghiệp vụ: chiết khấu thương mại trên hóa đơn, chiết khấu đạt SL/doanh số mua rồi mới hưởng, hoàn chiết
        khấu/hỗ trợ sau báo cáo, và chiết khấu thanh toán. Trường “Cách xác định giá trị chiết khấu” dùng để khai báo
        khoản chiết khấu tính theo %, theo tiền, theo số lượng hay theo doanh số.
      </ProcurementHintBox>

      <FormItemWrapper className='bw_row'>
        <FormItem className='bw_col_6' disabled={disabled} isRequired label='Tên chương trình chiết khấu'>
          <FormInput
            disabled={disabled}
            field='discount_program_name'
            placeholder='Nhập tên chương trình chiết khấu'
            validation={{
              required: 'Tên chương trình chiết khấu là bắt buộc',
            }}
            className='bw_mt_1'
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Đối tượng áp dụng'>
          <FormSelect
            list={ApplyPartnerTypeOptions}
            disabled={disabled}
            field='apply_partner_type_code'
            placeholder='Chọn đối tượng áp dụng'
            validation={{
              required: 'Đối tượng áp dụng là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired={isSupplierMode} label='Nhà cung cấp áp dụng'>
          <FormSelect
            list={supplierOptions}
            disabled={disabled}
            field='supplier_id'
            placeholder='Chọn nhà cung cấp'
            validation={{
              validate: (value) => {
                if (!isSupplierMode) {
                  return true;
                }
                return value ? true : 'Nhà cung cấp áp dụng là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired={isManufacturerMode} label='Hãng tài trợ / brand áp dụng'>
          <FormSelect
            list={mapDataOptions4Select(manufacturerData)}
            disabled={disabled}
            field='manufacture_id'
            placeholder='Chọn hãng nếu CTCK có hỗ trợ theo brand'
            validation={{
              validate: (value) => {
                if (!isManufacturerMode) {
                  return true;
                }
                return value ? true : 'Hãng / brand áp dụng là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Căn cứ tính chiết khấu'>
          <FormSelect
            list={DiscountBasisOptions}
            disabled={disabled}
            field='discount_basis_code'
            placeholder='Chọn căn cứ tính chiết khấu'
            validation={{
              required: 'Căn cứ tính chiết khấu là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Cách xác định giá trị chiết khấu'>
          <FormSelect
            list={ProcurementDiscountTypeOptions}
            disabled={disabled}
            field='procurement_discount_type_code'
            placeholder='Chọn cách xác định giá trị chiết khấu'
            validation={{
              required: 'Cách xác định giá trị chiết khấu là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Loại chiết khấu theo nghiệp vụ'>
          <FormSelect
            list={AccountingTreatmentOptions}
            disabled={disabled}
            field='accounting_treatment_code'
            placeholder='Chọn loại chiết khấu theo nghiệp vụ'
            validation={{
              required: 'Loại chiết khấu theo nghiệp vụ là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Chứng từ quyết toán'>
          <FormSelect
            list={SettlementDocumentOptions}
            disabled={disabled}
            field='settlement_document_code'
            placeholder='Chọn chứng từ quyết toán'
            validation={{
              required: 'Chứng từ quyết toán là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Thời điểm ghi nhận'>
          <FormSelect
            list={RecognitionTimingOptions}
            disabled={disabled}
            field='recognition_timing_code'
            placeholder='Chọn thời điểm ghi nhận'
            validation={{
              required: 'Thời điểm ghi nhận là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Nguyên tắc VAT'>
          <FormSelect
            list={VatAdjustmentModeOptions}
            disabled={disabled}
            field='vat_adjustment_mode_code'
            placeholder='Chọn nguyên tắc VAT'
            validation={{
              required: 'Nguyên tắc VAT là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Đối tượng quyết toán chiết khấu'>
          <FormSelect
            list={SettlementPartyOptions}
            disabled={disabled}
            field='settlement_party_code'
            placeholder='Chọn đối tượng sẽ quyết toán khoản chiết khấu'
            validation={{
              required: 'Đối tượng quyết toán chiết khấu là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='Ảnh hưởng giá vốn / tồn kho'>
          <FormSelect
            list={AffectsInventoryCostOptions}
            disabled={disabled}
            field='affects_inventory_cost'
            placeholder='Chọn tác động của khoản chiết khấu tới giá vốn'
            validation={{
              required: 'Ảnh hưởng giá vốn / tồn kho là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem
          className='bw_col_4'
          disabled={disabled}
          isRequired={!!affectsInventoryCost}
          label='Phân bổ điều chỉnh giá vốn'>
          <FormSelect
            list={CostAllocationMethodOptions}
            disabled={disabled}
            field='cost_allocation_method_code'
            placeholder='Chọn cách phân bổ khi chiết khấu nhận sau'
            validation={{
              validate: (value) => {
                if (!affectsInventoryCost) {
                  return true;
                }
                return value ? true : 'Phương pháp phân bổ điều chỉnh giá vốn là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired label='TK ghi nhận chiết khấu / hỗ trợ'>
          <FormSelect
            list={accountingAccountOptions}
            disabled={disabled}
            field='discount_account_id'
            placeholder='Chọn tài khoản ghi nhận chiết khấu'
            validation={{
              required: 'TK ghi nhận chiết khấu / hỗ trợ là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired={requiresPayableAccount} label='TK công nợ phải trả'>
          <FormSelect
            list={accountingAccountOptions}
            disabled={disabled}
            allowClear
            field='payable_account_id'
            placeholder='Chọn TK phải trả khi CTCK giảm công nợ'
            validation={{
              validate: (value) => {
                if (!requiresPayableAccount) {
                  return true;
                }
                return value ? true : 'TK công nợ phải trả là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} isRequired={requiresReceivableAccount} label='TK phải thu hoàn chiết khấu / hỗ trợ'>
          <FormSelect
            list={accountingAccountOptions}
            disabled={disabled}
            allowClear
            field='receivable_account_id'
            placeholder='Chọn TK phải thu hoàn chiết khấu / hỗ trợ từ hãng / NCC'
            validation={{
              validate: (value) => {
                if (!requiresReceivableAccount) {
                  return true;
                }
                return value ? true : 'TK phải thu hoàn chiết khấu / hỗ trợ là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem
          className='bw_col_4'
          disabled={disabled}
          isRequired={requiresVatAdjustmentAccount}
          label='TK điều chỉnh VAT đầu vào'>
          <FormSelect
            list={accountingAccountOptions}
            disabled={disabled}
            allowClear
            field='tax_adjustment_account_id'
            placeholder='Chọn TK VAT đầu vào điều chỉnh'
            validation={{
              validate: (value) => {
                if (!requiresVatAdjustmentAccount) {
                  return true;
                }
                return value ? true : 'TK điều chỉnh VAT đầu vào là bắt buộc';
              },
            }}
          />
        </FormItem>

        <FormItem className='bw_col_4' disabled={disabled} label='Ảnh hưởng công nợ'>
          <FormSelect
            list={DebitTypeOptions}
            disabled={disabled}
            field='debit_type'
            placeholder='Bỏ trống nếu chỉ giảm trực tiếp trên hóa đơn'
            allowClear
          />
        </FormItem>

        <FormItem className='bw_col_6' label='Thời gian áp dụng' isRequired disabled={disabled}>
          <FormRangePicker
            allowClear
            fieldStart='from_date'
            fieldEnd='to_date'
            placeholder={['Từ ngày', 'Đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
            validation={{
              required: 'Thời gian áp dụng là bắt buộc',
            }}
          />
        </FormItem>

        <FormItem className='bw_col_6' label='Đơn vị kinh doanh áp dụng' disabled={disabled}>
          <FormSelect
            mode='multiple'
            list={
              Array.isArray(businessData) && businessData.length > 0
                ? [allBusiness, ...mapDataOptions4Select(businessData)]
                : mapDataOptions4Select(businessData)
            }
            disabled={disabled}
            field='business_id'
            onChange={(e, o) => {
              let valueSet = e;
              if (e) {
                if (e[e.length - 1] == allBusiness?.value) {
                  const clonebusinessData = structuredClone(mapDataOptions4Select(businessData));
                  valueSet = mapArrayGetKey(clonebusinessData, 'value', allBusiness?.value, []);
                }
              }
              handleChangeCompanyBusiness(valueSet || null, true);
            }}
            // validation={{
            //   required: 'Miền áp dụng là bắt buộc',
            // }}
          />
        </FormItem>

        <FormItem className='bw_col_6' label='Khu vực áp dụng' disabled={disabled}>
          <FormSelect
            mode='multiple'
            list={mapDataOptions4Select(areaData)}
            disabled={disabled}
            field='area_list'
            // validation={{
            //   required: 'Khu vực áp dụng là bắt buộc',
            // }}
          />
        </FormItem>

        <FormItem className='bw_col_6' label='Ghi chú điều kiện chiết khấu' disabled={disabled}>
          <FormTextArea
            disabled={disabled}
            field='description'
            placeholder='Ví dụ: áp dụng khi buyer mua đủ MOQ, giữ nguyên công nợ 30 ngày, không cộng dồn với CTCK khác.'
          />
        </FormItem>

        <FormItem className='bw_col_6' label='Ghi chú hạch toán / quyết toán' disabled={disabled}>
          <FormTextArea
            disabled={disabled}
            field='accounting_note'
            placeholder='Ví dụ: nhà cung cấp xuất chứng từ giảm công nợ cuối tháng, kế toán ghi giảm công nợ phải trả.'
          />
        </FormItem>
      </FormItemWrapper>
    </BWAccordion>
  );
};

export default DiscountProgramInformation;
